import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar, formatIDR } from "@/components/AppTopBar";

export default function FinanceHealthAudit() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOwner = user?.role === "OWNER";
  const [summary, setSummary] = useState(null);
  const [fundRows, setFundRows] = useState([]);
  const [error, setError] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const firstOfYear = `${new Date().getFullYear()}-01-01`;
  const [fromDate, setFromDate] = useState(firstOfYear);
  const [toDate, setToDate] = useState(today);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!isOwner) return;
    setError("");
    Promise.all([api.get("/reports/dashboard"), api.get("/reports/fund-health")])
      .then(([d, fh]) => {
        setSummary(d.data);
        setFundRows(Array.isArray(fh.data?.rows) ? fh.data.rows : []);
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
  }, [isOwner]);

  const downloadAudit = async (kind) => {
    setBusy(kind); setError("");
    try {
      const url = `/exports/${kind === "inventory" ? "inventory-audit" : "finance-audit"}?from_date=${fromDate}&to_date=${toDate}`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `${kind}_audit_${fromDate}_to_${toDate}.xlsx`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch {
      setError("Export gagal. Pastikan rentang tanggal valid.");
    } finally {
      setBusy("");
    }
  };

  if (user && !isOwner) {
    return (<div className="app-shell"><AppTopBar active="reports" /><main className="content"><div className="alert-error">Akses ditolak.</div></main></div>);
  }

  const mismatch = fundRows.filter((r) => r.status !== "BALANCED");
  const healthItems = summary ? [
    { label: "Exception", count: summary.exception_count || 0 },
    { label: "Fund Mismatch", count: summary.fund_mismatch_count || mismatch.length },
    { label: "Piutang Open", count: summary.piutang_open_count || 0, amount: summary.piutang_open_amount, onClick: () => navigate("/finance/piutang") },
    { label: "Refund / Retur Menunggu", count: summary.refund_return_waiting || 0 },
  ] : [];

  return (
    <div className="app-shell">
      <AppTopBar active="reports" />
      <main className="content">
        <div style={{ width: "100%", maxWidth: 1180, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 18 }}>
            <div>
              <h1 style={{ margin: "0 0 6px", fontSize: 26 }}>Health Check & Audit</h1>
              <p className="hint" style={{ margin: 0 }}>Cek cepat masalah Finance. Jika semua aman, tidak ada tindakan yang diperlukan.</p>
            </div>
            <span className="hint">OWNER · CONTROL</span>
          </div>

          {error && <div className="alert-error">{error}</div>}

          <section className="panel panel--wide" style={{ marginTop: 0 }}>
            <h2 className="panel-title">Status Finance</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 12 }}>
              {healthItems.map((item) => {
                const alert = Number(item.count) > 0;
                const body = (
                  <>
                    <div className="meta-label">{item.label}</div>
                    <div className="mono" style={{ fontSize: 24, fontWeight: 700, marginTop: 7 }}>{item.count}</div>
                    {item.amount !== undefined && <div className="mono" style={{ marginTop: 3 }}>{formatIDR(item.amount)}</div>}
                    <div className="hint" style={{ marginTop: 6 }}>{item.onClick ? "LIHAT LAPORAN →" : (alert ? "PERLU DIPERIKSA" : "AMAN")}</div>
                  </>
                );
                const style = { border: `1px solid ${alert ? "#d99" : "var(--border)"}`, borderRadius: 10, padding: "14px 16px", background: "#fff", textAlign: "left", width: "100%" };
                return item.onClick ? (
                  <button key={item.label} type="button" onClick={item.onClick} style={{ ...style, cursor: "pointer" }} data-testid="open-receivables-report">{body}</button>
                ) : (
                  <div key={item.label} style={style}>{body}</div>
                );
              })}
            </div>
          </section>

          {mismatch.length > 0 && (
            <section className="panel panel--wide">
              <h2 className="panel-title">Fund Mismatch yang Perlu Diperiksa</h2>
              <div className="table-scroll"><table className="data-table">
                <thead><tr><th>Account</th><th>Fisik</th><th>Fund Total</th><th>Selisih</th><th>Status</th></tr></thead>
                <tbody>{mismatch.map((r) => <tr key={r.account_id}><td>{r.account_name}</td><td className="mono">{formatIDR(r.physical_balance)}</td><td className="mono">{formatIDR(r.fund_total)}</td><td className="mono">{formatIDR(r.difference)}</td><td><span className="status-pill off">{r.status}</span></td></tr>)}</tbody>
              </table></div>
            </section>
          )}

          <section className="panel panel--wide">
            <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div>
                <h2 className="panel-title">Audit Export</h2>
                <p className="hint" style={{ marginBottom: 0 }}>Read-only. Tidak membuat perubahan stok maupun keuangan.</p>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
                <div><label className="field-label">Dari</label><input type="date" className="field-input narrow" value={fromDate} max={toDate} onChange={(e) => setFromDate(e.target.value)} /></div>
                <div><label className="field-label">Sampai</label><input type="date" className="field-input narrow" value={toDate} min={fromDate} onChange={(e) => setToDate(e.target.value)} /></div>
                <button className="btn-mini" disabled={!!busy} onClick={() => downloadAudit("inventory")}>{busy === "inventory" ? "Menyiapkan…" : "Export Audit Inventory"}</button>
                <button className="btn-mini on" disabled={!!busy} onClick={() => downloadAudit("finance")}>{busy === "finance" ? "Menyiapkan…" : "Export Audit Finance"}</button>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
